import React, { useEffect } from 'react';
import * as S from './styles';
import UpgradeDetailsOnly from '../UpgradeDetailsOnly';
import ActionItemsList from '../ActionItemsList';
import NextStepsSection from '../NextStepsSection';
import { useTranslationHelper } from 'src/contexts/dependencyManager/utils';
import { IconChevronRight, InlineNotification } from '@veneer/core';
import { PrinterItem } from 'src/utils/Products/productFilter';
import usePendingChanges from 'src/hooks/usePendingChanges';
import useSubscriptionId from 'src/hooks/useSubscriptionId';
import useShellContext from 'src/hooks/useShellContext/useShellContext';
import getEnvironmentVariables from 'src/helpers/getEnvironmentVariables';
import { getStack } from 'src/helpers/getStack';

interface UpgradeConfirmationProps {
  orderNumber: string;
  selectedPrinterDetails: PrinterItem;
}

const UpgradeConfirmation: React.FC<UpgradeConfirmationProps> = ({
  orderNumber,
  selectedPrinterDetails
}) => {
  const { authProvider, stack } = useShellContext();
  const subscriptionId = useSubscriptionId();
  const env = getEnvironmentVariables(getStack(stack));
  const getText = useTranslationHelper();

  const { isPlanChangeDone, fetchPendingChanges } = usePendingChanges(
    authProvider,
    stack,
    subscriptionId
  );

  useEffect(() => {
    fetchPendingChanges();
  }, [subscriptionId]);

  return (
    <S.Container>
      <S.HeaderContainer>
        <S.MainTitle>
          {getText('confirmation.title', 'Your upgrade is confirmed!')}
        </S.MainTitle>
      </S.HeaderContainer>

      <S.MainContent>
        <S.ContentSection>
          <UpgradeDetailsOnly
            selectedPrinterDetails={selectedPrinterDetails}
            isPlanChangeDone={isPlanChangeDone}
          />
          <S.NotificationItem>
            <InlineNotification
              type="informative"
              closeButton={false}
              title={getText(
                'confirmation.checkItem1',
                'Continue to use your current printer.'
              )}
              description={getText(
                'confirmation.checkItem2',
                'Your new printer is expected to be delivered to your door within 2 business days.'
              )}
            />
          </S.NotificationItem>
          <ActionItemsList orderNumber={orderNumber} />
        </S.ContentSection>
      </S.MainContent>

      <NextStepsSection />
      {env?.variables?.SHOW_SURVEY_LINK && (
        <S.SurveySectionWrapper>
          <S.SurveySection>
            <S.SurveyText>
              {getText(
                'confirmation.giveFeedback',
                'Give us your feedback with a quick survey'
              )}
            </S.SurveyText>
            <IconChevronRight filled />
          </S.SurveySection>
        </S.SurveySectionWrapper>
      )}
    </S.Container>
  );
};

export default UpgradeConfirmation;
